// Your account (spec/13 §2's staff menu): what the signed-in member manages about themselves, calm and
// in sections rather than one long form (BRAND.md §6.1). Each change opens its own dialog or acts on
// one row:
//
//   - who you are, as the session knows you;
//   - your staff PIN at each venue of this organisation you work at (F-IAM-10) — the server's list
//     (`listMyStaffPins`), so an owner or an every-venue member sees every live venue — with whether it
//     is set there; only you ever set it;
//   - the second factor: new backup codes, or turning it off, each confirmed with your password
//     (F-IAM-09);
//   - where you are signed in, each device ended on its own (F-IAM-12);
//   - Google, Discord and Steam linked to your account, or unlinked (F-IAM-03 … F-IAM-05; organisation console
//     only, `ConnectedAccounts.tsx`);
//   - which notifications reach you here, and quiet hours (F-NTF-01).
//
// Reached from the staff menu at a venue (`/v/{slug}/account`) or at the organisation
// (`/o/{slug}/account`); nothing here needs a permission, because everything is about the caller.

import type { NotificationCategory, NotificationChannel, NotificationPreferences, SignedInSession } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Checkbox, SegmentedControl, Skeleton, Switch, TextField, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../api/problems';
import { useAccess, useMyVenues, useSession } from '../api/queries';
import { useProblem } from '../api/use-problem';
import { useSendDailySummary } from '../org/reports/queries';
import { LoadFailure } from '../shell/LoadFailure';
import { Icon } from '../shell/nav-icons';
import { useSignOut } from '../shell/AppShell';
import { useScopeIds } from '../shell/scope';
import { CONSOLE_THEMES, setConsoleTheme, useConsoleTheme, type ConsoleTheme } from '../shell/theme';
import { Facts, Notice, Page, Section, SettingRow } from '../ui';
import { StaffPinDialog, TwoFactorDialog, type TwoFactorAction } from './AccountDialogs';
import { ConnectedAccountsSection } from './ConnectedAccounts';
import { useMySessions, useMyStaffPins, useNotificationPreferences, usePutNotificationPreferences, useRevokeMySession } from './queries';

import './account.css';

/** A staff member's notification categories (spec/16 §4.2; the contract's `STAFF_NOTIFICATION_CATEGORIES`). */
const STAFF_CATEGORIES = ['floor', 'orders', 'payments', 'stock', 'shifts', 'staffing', 'account', 'security', 'insights'] as const satisfies readonly NotificationCategory[];

/** Transactional categories stay on in the inbox whatever is sent (spec/16 §3), so their inbox switch is fixed. */
const TRANSACTIONAL: ReadonlySet<string> = new Set(['orders', 'payments', 'security']);

/** The channels a staff member switches per category here: the console's inbox and the staff app's push. */
const CHANNELS = ['inbox', 'push'] as const satisfies readonly NotificationChannel[];

export function AccountPage(): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Page title={t('account.title')} description={t('account.intro')}>
      <div className="console-account">
        <ProfileSection />
        <AppearanceSection />
        <StaffPinSection />
        <TwoFactorSection />
        <ConnectedAccountsSection />
        <DevicesSection />
        <NotificationsSection />
        <DailySummarySection />
      </div>
    </Page>
  );
}

function ProfileSection(): ReactNode {
  const { t } = useTranslation('console');
  const user = useSession().data?.user;
  return (
    <Section title={t('account.profile.title')} description={t('account.profile.hint')}>
      {user === undefined ? (
        <Skeleton shape="text" lines={2} />
      ) : (
        <Facts
          items={[
            { key: 'name', label: t('account.profile.name'), value: user.name || t('account.profile.noName') },
            { key: 'email', label: t('account.profile.email'), value: user.email || t('account.profile.noEmail') },
          ]}
        />
      )}
    </Section>
  );
}

// -------------------------------------------------------------------------------------------------
// Staff PIN (F-IAM-10)
// -------------------------------------------------------------------------------------------------

/**
 * The venues are the server's answer (`GET …/members/me/pins`), never worked out here: every live venue
 * for an owner or a member with no venue scope, their own venues otherwise — the venues the server takes
 * a PIN for, each with whether it is set.
 */
/** Dark, light or this device's own (ADR-0041): a choice on this device only, dark until one is made. */
function AppearanceSection(): ReactNode {
  const { t } = useTranslation('console');
  const { theme } = useConsoleTheme();
  return (
    <Section title={t('account.appearance.title')} description={t('account.appearance.hint')} body="rows">
      <SettingRow
        label={t('account.appearance.label')}
        control={
          <SegmentedControl<ConsoleTheme>
            label={t('account.appearance.label')}
            value={theme}
            onValueChange={setConsoleTheme}
            options={CONSOLE_THEMES.map((value) => ({ value, label: t(`account.appearance.${value}`) }))}
          />
        }
      />
    </Section>
  );
}

function StaffPinSection(): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const pins = useMyStaffPins(scope.organizationId);
  const [venue, setVenue] = useState<{ readonly id: string; readonly name: string } | null>(null);

  return (
    <Section title={t('account.pin.title')} description={t('account.pin.hint')} body="rows">
      {pins.data === undefined ? (
        <div className="console-account__pad">
          {pins.isError ? (
            <LoadFailure error={pins.error} message={t('account.pin.unavailable')} onRetry={() => void pins.refetch()} />
          ) : (
            <Skeleton shape="text" lines={2} />
          )}
        </div>
      ) : pins.data.length === 0 ? (
        <p className="console-account__pad console-muted">{t('account.pin.noVenues')}</p>
      ) : (
        <ul className="console-account__rows" aria-label={t('account.pin.listLabel')}>
          {pins.data.map((one) => (
            <li key={one.venueId} className="console-account__row">
              <span className="console-account__row-main">
                <span className="console-account__row-title">
                  {one.venueName}
                  <Badge tone={one.locked ? 'danger' : one.set ? 'ok' : 'neutral'} dot>
                    {one.locked ? t('account.pin.isLocked') : one.set ? t('account.pin.isSet') : t('account.pin.notSet')}
                  </Badge>
                </span>
                {one.locked ? (
                  <span className="console-subtle">{t('account.pin.lockedRow')}</span>
                ) : one.venueId === scope.venueId ? (
                  <span className="console-subtle">{t('account.pin.thisVenue')}</span>
                ) : null}
              </span>
              {/* A locked PIN takes no new one until a manager resets it (spec/07 §6.7): nothing to press. */}
              {one.locked ? null : (
                <Button
                  variant={one.set ? 'ghost' : 'secondary'}
                  size="sm"
                  aria-label={t(one.set ? 'account.pin.changeFor' : 'account.pin.setFor', { venue: one.venueName })}
                  onClick={() => setVenue({ id: one.venueId, name: one.venueName })}
                >
                  {one.set ? t('account.pin.change') : t('account.pin.set')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <StaffPinDialog organizationId={scope.organizationId} venue={venue} onClose={() => setVenue(null)} />
    </Section>
  );
}

// -------------------------------------------------------------------------------------------------
// The second factor (F-IAM-09)
// -------------------------------------------------------------------------------------------------

function TwoFactorSection(): ReactNode {
  const { t } = useTranslation('console');
  const session = useSession().data;
  const [action, setAction] = useState<TwoFactorAction | null>(null);
  if (session === undefined || session === null) return null;
  const enabled = session.user.twoFactorEnabled === true;

  return (
    <Section
      title={t('account.twoFactor.title')}
      description={t('account.twoFactor.hint')}
      actions={
        <Badge tone={enabled ? 'ok' : 'neutral'} dot>
          {enabled ? t('account.twoFactor.on') : t('account.twoFactor.off')}
        </Badge>
      }
    >
      {enabled ? (
        <div className="console-form__actions">
          <Button variant="secondary" icon={<Icon name="lock" />} onClick={() => setAction('codes')}>
            {t('account.twoFactor.newCodes')}
          </Button>
          <Button variant="ghost" onClick={() => setAction('disable')}>
            {t('account.twoFactor.disable')}
          </Button>
        </div>
      ) : (
        <p className="console-muted">{t('account.twoFactor.offHint')}</p>
      )}
      <TwoFactorDialog action={action} onClose={() => setAction(null)} />
    </Section>
  );
}

// -------------------------------------------------------------------------------------------------
// Signed-in devices (F-IAM-12)
// -------------------------------------------------------------------------------------------------

function DevicesSection(): ReactNode {
  const { t } = useTranslation('console');
  const sessions = useMySessions();

  return (
    <Section title={t('account.devices.title')} description={t('account.devices.hint')} body="rows">
      {sessions.data === undefined ? (
        sessions.isError ? (
          <div className="console-account__pad">
            <LoadFailure error={sessions.error} message={t('account.devices.unavailable')} onRetry={() => void sessions.refetch()} />
          </div>
        ) : (
          <div className="console-account__pad">
            <Skeleton shape="text" lines={3} />
          </div>
        )
      ) : (
        <ul className="console-account__rows" aria-label={t('account.devices.listLabel')}>
          {sessions.data.map((session) => (
            <DeviceRow key={session.id} session={session} />
          ))}
        </ul>
      )}
    </Section>
  );
}

function DeviceRow({ session }: { readonly session: SignedInSession }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const { show } = useToast();
  const problems = useProblem();
  const revoke = useRevokeMySession();
  const signOut = useSignOut();
  const [confirming, setConfirming] = useState(false);
  const device = session.device ?? t('account.devices.unknownDevice');

  const end = () =>
    revoke.mutate(session.id, {
      onSuccess: () => {
        if (session.current) {
          void signOut();
          return;
        }
        show({ tone: 'ok', title: t('account.devices.ended') });
      },
      onError: (error) => {
        setConfirming(false);
        show({ tone: 'danger', title: problems.text(error, { fallback: t('account.devices.failed') }) });
      },
    });

  return (
    <li className="console-account__row">
      <span className="console-account__row-main">
        <span className="console-account__row-title">
          {device}
          {session.current ? (
            <Badge tone="info" dot>
              {t('account.devices.current')}
            </Badge>
          ) : null}
        </span>
        <span className="console-subtle">
          {[
            t('account.devices.lastActive', { when: format.dateTime(session.lastActiveAt, { date: 'medium', time: 'short' }) }),
            session.ipAddress,
            session.secondFactor ? t('account.devices.secondFactor') : null,
          ]
            .filter((part): part is string => part !== null && part !== '')
            .join(' · ')}
        </span>
      </span>
      {confirming ? (
        <span className="console-account__confirm" role="group" aria-label={t('account.devices.confirmLabel', { device })}>
          <span className="console-muted">{session.current ? t('account.devices.confirmCurrent') : t('account.devices.confirm')}</span>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" size="sm" busy={revoke.isPending} onClick={end}>
            {t('account.devices.end')}
          </Button>
        </span>
      ) : (
        <Button variant="ghost" size="sm" aria-label={t('account.devices.endFor', { device })} onClick={() => setConfirming(true)}>
          {t('account.devices.end')}
        </Button>
      )}
    </li>
  );
}

// -------------------------------------------------------------------------------------------------
// The daily summary (N-27, F-ANL-07): an owner's opt-in, for the organisation and for each venue
// -------------------------------------------------------------------------------------------------

/**
 * Owners only: nothing here for any other role, and the server refuses a subscription from one. Off until switched on —
 * the organisation's summary (one email once every venue has closed its day) and any venue's (as soon as its day is
 * closed) — with "Email me the latest summary" to see one now.
 */
function DailySummarySection(): ReactNode {
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  if (access.status !== 'ready' || access.role !== 'owner') return null;
  return <DailySummaryOwnerSection organizationId={scope.organizationId} />;
}

function DailySummaryOwnerSection({ organizationId }: { readonly organizationId: string }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const preferences = useNotificationPreferences(organizationId);
  const put = usePutNotificationPreferences(organizationId);
  const problems = useProblem();
  const send = useSendDailySummary(organizationId);
  const email = useSession().data?.user.email;
  const venues = (useMyVenues().data ?? []).filter((venue) => venue.organizationId === organizationId);

  const subscriptions = preferences.data?.subscriptions ?? [];
  const subscribed = (venueId: string | null) => subscriptions.some((one) => one.code === 'N-27' && one.venueId === venueId);
  const toggle = (venueId: string | null, on: boolean) => {
    const rest = subscriptions.filter((one) => !(one.code === 'N-27' && one.venueId === venueId)).map((one) => ({ code: one.code, venueId: one.venueId }));
    put.mutate({ subscriptions: on ? [...rest, { code: 'N-27', venueId }] : rest }, { onError: (error) => show({ tone: 'danger', title: problems.text(error, { fallback: t('account.dailySummary.failed') }) }) });
  };
  const sendNow = () =>
    send.mutate(
      {},
      {
        onSuccess: () => show({ tone: 'ok', title: t('account.dailySummary.sent') }),
        onError: (error) => show({ tone: 'danger', title: problemCode(error) === PROBLEM.emailNotVerified ? t('account.dailySummary.unverified') : problems.text(error, { fallback: t('account.dailySummary.sendFailed') }) }),
      },
    );

  return (
    <Section title={t('account.dailySummary.title')} description={t('account.dailySummary.hint')}>
      {preferences.data === undefined ? (
        preferences.isError ? (
          <LoadFailure error={preferences.error} message={t('account.dailySummary.unavailable')} onRetry={() => void preferences.refetch()} />
        ) : (
          <Skeleton shape="text" lines={3} />
        )
      ) : (
        <div className="console-account__notifications">
          <SettingRow
            label={t('account.dailySummary.organization')}
            description={t('account.dailySummary.organizationHint')}
            control={
              <Switch
                label={<span className="clav-visually-hidden">{t('account.dailySummary.organization')}</span>}
                checked={subscribed(null)}
                disabled={put.isPending}
                onCheckedChange={(on) => toggle(null, on)}
              />
            }
          />
          <SettingRow
            label={t('account.dailySummary.venues')}
            description={t('account.dailySummary.venuesHint')}
            layout="stack"
            control={
              venues.length === 0 ? (
                <p className="console-subtle">{t('account.dailySummary.noVenues')}</p>
              ) : (
                <ul className="console-account__venue-picks" aria-label={t('account.dailySummary.venuesLabel')}>
                  {venues.map((venue) => (
                    <li key={venue.id}>
                      <Checkbox label={venue.name} checked={subscribed(venue.id)} disabled={put.isPending} onCheckedChange={(on) => toggle(venue.id, on === true)} />
                    </li>
                  ))}
                </ul>
              )
            }
          />
          <p className="console-subtle">{email === undefined || email === '' ? t('account.dailySummary.whereNoEmail') : t('account.dailySummary.where', { email })}</p>
          <div className="console-form__actions">
            <Button variant="secondary" size="sm" icon={<Icon name="mail" />} busy={send.isPending} onClick={sendNow}>
              {t('account.dailySummary.sendNow')}
            </Button>
            <span className="console-subtle">{t('account.dailySummary.sendNowHint')}</span>
          </div>
        </div>
      )}
    </Section>
  );
}

// -------------------------------------------------------------------------------------------------
// Notifications (F-NTF-01)
// -------------------------------------------------------------------------------------------------

function NotificationsSection(): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const preferences = useNotificationPreferences(scope.organizationId);

  return (
    <Section title={t('account.notifications.title')} description={t('account.notifications.hint')}>
      {preferences.data === undefined ? (
        preferences.isError ? (
          <LoadFailure error={preferences.error} message={t('account.notifications.unavailable')} onRetry={() => void preferences.refetch()} />
        ) : (
          <Skeleton shape="text" lines={4} />
        )
      ) : (
        <NotificationsForm organizationId={scope.organizationId} preferences={preferences.data} />
      )}
    </Section>
  );
}

/** Whether a category reaches you on a channel: your override, else the default — on, for staff (spec/16 §3). */
function enabledFor(preferences: NotificationPreferences, category: string, channel: string): boolean {
  if (channel === 'inbox' && TRANSACTIONAL.has(category)) return true;
  const override = preferences.overrides.find((one) => one.category === category && one.channel === channel);
  return override?.enabled ?? true;
}

function NotificationsForm({ organizationId, preferences }: { readonly organizationId: string; readonly preferences: NotificationPreferences }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const problems = useProblem();
  const put = usePutNotificationPreferences(organizationId);
  const [quiet, setQuiet] = useState(preferences.quietHours);
  const [quietError, setQuietError] = useState<string | undefined>(undefined);
  const quietChanged =
    quiet.enabled !== preferences.quietHours.enabled || quiet.start !== preferences.quietHours.start || quiet.end !== preferences.quietHours.end;

  const failed = (error: unknown) => show({ tone: 'danger', title: problems.text(error, { fallback: t('account.notifications.failed') }) });

  const toggle = (category: NotificationCategory, channel: NotificationChannel, enabled: boolean) => {
    const rest = preferences.overrides.filter((one) => !(one.category === category && one.channel === channel));
    put.mutate({ overrides: [...rest, { category, channel, enabled }] }, { onError: failed });
  };

  const saveQuiet = () => {
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(quiet.start) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(quiet.end)) {
      setQuietError(t('account.notifications.quietInvalid'));
      return;
    }
    setQuietError(undefined);
    put.mutate({ quietHours: quiet }, { onSuccess: () => show({ tone: 'ok', title: t('account.notifications.saved') }), onError: failed });
  };

  return (
    <div className="console-account__notifications">
      <SettingRow
        label={t('account.notifications.quiet')}
        description={t('account.notifications.quietHint')}
        // The row's label names the switch; written again beside it, it said "Quiet hours" twice.
        control={
          <Switch
            label={<span className="clav-visually-hidden">{t('account.notifications.quiet')}</span>}
            checked={quiet.enabled}
            onCheckedChange={(enabled) => setQuiet({ ...quiet, enabled })}
          />
        }
      />
      {quiet.enabled ? (
        <div className="console-form__row">
          <TextField type="time" label={t('account.notifications.from')} value={quiet.start} onChange={(event) => setQuiet({ ...quiet, start: event.target.value })} />
          <TextField type="time" label={t('account.notifications.until')} value={quiet.end} onChange={(event) => setQuiet({ ...quiet, end: event.target.value })} />
        </div>
      ) : null}
      {quietError === undefined ? null : <Notice tone="danger" role="alert" title={quietError} />}
      {quietChanged ? (
        <div className="console-form__actions">
          <Button variant="primary" size="sm" busy={put.isPending} onClick={saveQuiet}>
            {t('account.notifications.saveQuiet')}
          </Button>
        </div>
      ) : null}

      <table className="console-account__matrix">
        <caption className="clav-visually-hidden">{t('account.notifications.matrix')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('account.notifications.category')}</th>
            {CHANNELS.map((channel) => (
              <th key={channel} scope="col">
                {t(`account.notifications.channel.${channel}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {STAFF_CATEGORIES.map((category) => (
            <tr key={category}>
              <th scope="row">{t(`account.notifications.categories.${category}`)}</th>
              {CHANNELS.map((channel) => {
                const fixed = channel === 'inbox' && TRANSACTIONAL.has(category);
                const label = t('account.notifications.toggle', {
                  category: t(`account.notifications.categories.${category}`),
                  channel: t(`account.notifications.channel.${channel}`),
                });
                return (
                  <td key={channel}>
                    <Switch
                      label={<span className="clav-visually-hidden">{label}</span>}
                      checked={enabledFor(preferences, category, channel)}
                      disabled={fixed || put.isPending}
                      onCheckedChange={(enabled) => toggle(category, channel, enabled)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="console-subtle">{t('account.notifications.alwaysOn')}</p>
    </div>
  );
}
